import { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { checkoutRemove, checkoutUpdateQty } from "../../features/checkoutSlice";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaTimes,
  FaPlus,
  FaMinus,
  FaTrash,
  FaShoppingCart,
} from "react-icons/fa";

const TemporarySelected = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.login.data);
  const dispatch = useDispatch();
  const basket = useSelector((state) => state.checkout.items);
  const panelRef = useRef(null);

  // Derive currency from user country
  const currency = user?.country === "Senegal" ? "XOF" : user?.country === "Ethiopia" ? "ETB" : "RWF";

  // Calculate total items and price
  const totalItems = basket.reduce(
    (sum, item) => sum + (item.qty || 1),
    0,
  );
  const totalPrice = basket.reduce(
    (sum, item) =>
      sum + parseFloat(item.price_per_pc || 0) * (item.qty || 1),
    0,
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        props.close();
      }
    };
    if (props.open) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [props.open]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && props.open) {
        props.close();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [props.open]);

  // ACU-204: Fixed payload structure { id, qty }
  const updateQuantity = (testId, newQuantity) => {
    if (newQuantity < 1) return;
    dispatch(checkoutUpdateQty({ id: testId, qty: newQuantity }));
  };

  const removeItem = (testId) => {
    dispatch(checkoutRemove(testId));
  };

  const proceedToCheckout = () => {
    props.close();
    navigate("/collection/order");
  };

  if (!props.open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${
          props.open ? "bg-opacity-50" : "bg-opacity-0"
        }`}
        onClick={props.close}
      />
      <div
        ref={panelRef}
        className={`absolute right-0 top-0 h-full w-full md:w-96 bg-white shadow-2xl transform transition-all duration-300 ease-in-out ${
          props.open ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-[#1c7d7f] to-[#166366] text-white">
          <div className="flex items-center gap-3">
            <FaShoppingCart size={20} />
            <h3 className="text-lg font-semibold">{t('basket.title')}</h3>
            <span className="bg-white text-[#1c7d7f] text-xs font-bold px-2 py-1 rounded-full">
              {totalItems} {totalItems === 1 ? t('basket.item') : t('basket.items')}
            </span>
          </div>
          <button
            onClick={props.close}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            aria-label={t('basket.close')}
          >
            <FaTimes size={18} />
          </button>
        </div>

        <div className="flex flex-col h-[calc(100vh-80px)]">
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {basket.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <FaShoppingCart size={48} className="mb-4 opacity-50" />
                <p className="text-lg font-medium">{t('basket.empty')}</p>
                <p className="text-sm">{t('basket.empty_hint')}</p>
              </div>
            ) : (
              basket.map((item, index) => (
                <div
                  key={index}
                  className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-all duration-200"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-800 truncate mb-1">
                        {item.testName}
                      </h4>
                      <p className="text-sm text-gray-600 mb-1">
                        {item.facilityName}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-[#1c7d7f]">
                          {parseFloat(item.price_per_pc || 0).toFixed(2)} {currency}
                        </span>
                        <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-300">
                          <button
                            onClick={() =>
                              updateQuantity(item.testId, (item.qty || 1) - 1)
                            }
                            className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-l-lg transition-colors"
                            aria-label={t('basket.decrease')}
                            disabled={(item.qty || 1) <= 1}
                          >
                            <FaMinus size={12} />
                          </button>
                          <span className="px-3 py-1 font-medium min-w-[3rem] text-center">
                            {item.qty || 1}
                          </span>
                          <button
                            onClick={() =>
                              updateQuantity(item.testId, (item.qty || 1) + 1)
                            }
                            className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-r-lg transition-colors"
                            aria-label={t('basket.increase')}
                          >
                            <FaPlus size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeItem(item.testId)}
                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      aria-label={t('basket.remove_item')}
                    >
                      <FaTrash size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {basket.length > 0 && (
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <div className="flex justify-between items-center mb-3">
                <span className="text-gray-600">{t('basket.total')}</span>
                <span className="text-xl font-bold text-[#1c7d7f]">
                  {totalPrice.toFixed(2)} {currency}
                </span>
              </div>
              <button
                onClick={proceedToCheckout}
                className="w-full bg-[#1c7d7f] text-white py-3 rounded-lg font-semibold hover:bg-[#166366] transition-colors focus:outline-none focus:ring-4 focus:ring-[#1c7d7f] focus:ring-opacity-50"
              >
                {t('basket.proceed')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemporarySelected;